import { Component, OnInit, Input, ViewEncapsulation, Output, EventEmitter } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MeterDataStoreServiceDirective } from '@app/shared/services/store/meter-data-store.service';
import { MeterView } from '@app/shared/models/meter-data/meter-view';
import { MeterDataService } from '@app/shared/services/api/meter-data.service';
import { isNil } from 'lodash';
import { AuthService } from '@app/shared/services/auth.service';
import { GetAddressPipe } from '@app/shared/pipes/get-address.pipe';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { CheckedMeter } from '@app/shared/models/meter/meter-wizard-checked-meter';

@Component({
  selector: 'app-meter-wizard-collapsible-row',
  templateUrl: './meter-wizard-collapsible-row.component.html',
  styleUrls: ['./meter-wizard-collapsible-row.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [GetAddressPipe],
})
export class MeterWizardCollapsibleRowComponent implements OnInit {
  @Input() isChecked = false;
  @Input() data: MeterView[] = [];
  @Input() energyGuardData: any;
  @Input() hasDeviceEUI = false;
  @Output() checkMeter$: EventEmitter<CheckedMeter> = new EventEmitter<CheckedMeter>();

  sortedData: MeterView[] = [];
  shown = false;
  hasChild = false;
  notesUpdated = false;
  isFilterByBuilding = false;

  constructor(
    public meterDataStoreServiceDirective: MeterDataStoreServiceDirective,
    public authService: AuthService,
    private router: Router,
    private meterDataService: MeterDataService,
    private userMunicipalitiesService: UserMunicipalitiesService,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.data.forEach((a) => {
      if (isNil(a.objectType)) {
        a.objectType = '';
      }
    });
    this.sortedData = this.data;
    if (this.sortedData.length > 1) {
      this.hasChild = true;
    }

    this.isFilterByBuilding = this.route.snapshot.queryParamMap.get('action') === 'filterbybuilding';
    if (this.isFilterByBuilding) {
      this.shown = true;
    }
  }

  saveNotes(row: any) {
    row.child.toggleLoaderVisibility();
    this.meterDataService
      .updateNotesById(
        {
          guid: row.guid,
          notes: row.notes,
        },
        this.userMunicipalitiesService.currentMunicipality.key
      )
      .subscribe(() => {
        row.child.toggleLoaderVisibility();
      });
  }

  navigateToConsumptionPage(guid: string) {
    const meterView = this.data.find((x) => x.guid === guid) as MeterView;

    this.router.navigate(['/verbrauch'], {
      queryParams: {
        connectionobjectnumber: meterView.connectionObjectNumber,
        guid: meterView.guid,
        returnroute: 'messdaten/energiedaten',
      },
      queryParamsHandling: 'merge',
    });
  }

  tryGetEnergyGuardData(meter: MeterView) {
    const data = this.energyGuardData ? this.energyGuardData[meter.meterNumber] : null;
    return !isNil(data)
      ? data
      : {
          nilCheck: true,
          isEnergyGuardLicenseAvailable: false,
          getAlarmCount: () => 0,
          isBadgeVisible: () => false,
        };
  }

  energyDataIsLoading() {
    return this.sortedData.find((d) => this.tryGetEnergyGuardData(d).nilCheck);
  }

  getMeterTypeImage(code: string, deviceEUI: string) {
    return this.meterDataStoreServiceDirective.getMeterTypeImage(code, deviceEUI);
  }

  getEnergyTypeImage(name: string) {
    return this.meterDataStoreServiceDirective.getEnergyTypeImage(name);
  }

  getEnergyGuardData(meter: MeterView) {
    return this.tryGetEnergyGuardData(meter);
  }

  isGroupLicensed() {
    return !isNil(this.sortedData.find((d) => this.tryGetEnergyGuardData(d).isEnergyGuardLicenseAvailable));
  }

  getGroupAlarmCount() {
    return this.sortedData.reduce((total, d) => total + this.tryGetEnergyGuardData(d).getAlarmCount(), 0);
  }

  isGroupBadgeVisible() {
    return this.getGroupAlarmCount() > 1;
  }

  public onCheckboxChange(event: any): void {
    this.checkMeter$.emit({
      address: event.source.value.getAddress(true).replace('\r\n', ' '),
      checked: event.checked,
      converterFactor: event.source.value.converterFactor,
      deviceId: event.source.value.deviceId,
      energyTypesName: event.source.value.energyTypesName,
      id: event.source.id,
      marketLocationKey: event.source.value.marketLocationKey,
      marketOperatorCategory: event.source.value.getMarketOperatorCategory(),
      meterNumber: event.source.value.meterNumber,
      meterOperator: event.source.value.getMarketOperatorText(),
      meterTypeCode: event.source.value.meterTypeCode,
      meterTypesName: event.source.value.meterTypesName,
    } as CheckedMeter);
  }
}
